import actions from 'actions/Actions';
import store from 'store/Store';

import localeUtils from 'utils/LanguageLocale';
import { classifyPasskeyError, logPasskeyForDynatrace } from 'utils/passkeyDynatrace';

import anaConsts from 'analytics/constants';
import { UPDATE_PASSKEYS } from 'constants/actionTypes/user';

import PasskeyService from 'services/PasskeyService';
import processEvent from 'analytics/processEvent';

import PasskeysActionsEN from 'actions/locales/PasskeysActions_en_US';
import PasskeysActionsFR from 'actions/locales/PasskeysActions_fr_CA';
const getText = localeUtils.getLocalisedDataFromResource(PasskeysActionsEN, PasskeysActionsFR);

const getAvailablePasskeysManually = (passkeys, removedPasskey) => passkeys.filter(passkey => passkey.passkeyId !== removedPasskey.passkeyId);

const removePasskey = (passkeys, passkeyToRemove, userData) => {
    store.dispatch(actions.showInterstice(true));

    PasskeyService.removePasskey(passkeyToRemove.passkeyId, userData)
        .then(removePasskeyResponse => {
            if (removePasskeyResponse.responseStatus !== 200 || removePasskeyResponse?.errors?.[0]?.errorCode) {
                const errData = removePasskeyResponse?.errors?.[0];
                const apiError = new Error(errData?.errorMessage || 'Remove passkey failed');
                apiError.errorCode = errData?.errorCode;
                throw apiError;
            }

            // Analytics
            processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                data: {
                    eventStrings: [`${anaConsts.Event.EVENT_289}=1`]
                }
            });

            return PasskeyService.getPasskeys()
                .then(getPasskeysResponse => {
                    if (!getPasskeysResponse || getPasskeysResponse?.errors?.[0]?.errorCode) {
                        throw new Error(getPasskeysResponse.errors[0].errorMessage);
                    }

                    return processNewPasskeys(getPasskeysResponse);
                })
                .catch(refreshError => {
                    logPasskeyForDynatrace('API', 'GetPasskeys', refreshError, 'account-settings');
                    import('utils/BeyondIdentity')
                        .then(BeyondIdentityUtils => {
                            BeyondIdentityUtils.default.trackPasskeyError(
                                getText('anErrorOccurredPleaseTryAgainLater'),
                                refreshError,
                                'api:getpasskeys'
                            );
                        })
                        .catch(() => {});
                    const manuallyRemovedData = getAvailablePasskeysManually(passkeys, passkeyToRemove);

                    return processNewPasskeys(manuallyRemovedData);
                });
        })
        .catch(error => {
            store.dispatch(actions.showInterstice(false));
            logPasskeyForDynatrace('API', 'RemovePasskey', error, 'account-settings');

            const errorMessage = getText('anErrorOccurredPleaseTryAgainLater');

            // Track passkey removal error with Adobe Analytics
            import('utils/BeyondIdentity')
                .then(BeyondIdentityUtils => {
                    BeyondIdentityUtils.default.trackPasskeyError(errorMessage, error, 'api:remove');
                })
                .catch(importErr => {
                    Sephora?.logger?.warn?.('[Passkey Analytics] Failed to load BeyondIdentity for tracking', importErr);
                });

            return store.dispatch(
                actions.showInfoModal({
                    isOpen: true,
                    title: getText('error'),
                    message: errorMessage,
                    buttonText: getText('ok')
                })
            );
        });
};

const openRemovePasskeysModal = (passkeys, passkeyToRemove, message, userData) =>
    store.dispatch(
        actions.showInfoModal({
            isOpen: true,
            title: getText('removePasskeysTitle'),
            message: message(getText),
            footerColumns: 1,
            buttonText: getText('removePasskey'),
            callback: () => removePasskey(passkeys, passkeyToRemove, userData),
            showCancelButton: true,
            showCloseButton: true
        })
    );

const processNewPasskeys = newPasskeys => {
    const passkeyArray = Object.values(newPasskeys).filter(item => item.passkeyId);
    store.dispatch(updatePasskeys(passkeyArray));

    store.dispatch(actions.showInterstice(false));

    store.dispatch(
        actions.showInfoModal({
            isOpen: true,
            title: getText('passkeyRemoved'),
            message: getText('yourPasskeyHaveBeenRemoved'),
            buttonText: getText('ok')
        })
    );
};

const getPasskeysData = () => {
    store.dispatch(actions.showInterstice(true));

    return PasskeyService.getPasskeys()
        .then(resp => {
            const passkeyArray = Object.values(resp).filter(item => item.passkeyId);
            store.dispatch(updatePasskeys(passkeyArray));

            return passkeyArray;
        })
        .catch(err => {
            logPasskeyForDynatrace(classifyPasskeyError(err), 'GetPasskeys', err, 'data-fetch');
            import('utils/BeyondIdentity')
                .then(BeyondIdentityUtils => {
                    BeyondIdentityUtils.default.trackPasskeyError(getText('anErrorOccurredPleaseTryAgainLater'), err, 'api:getpasskeys');
                })
                .catch(() => {});

            return null;
        })
        .finally(() => {
            store.dispatch(actions.showInterstice(false));
        });
};

const updatePasskeys = data => ({
    type: UPDATE_PASSKEYS,
    payload: data
});

export default {
    getAvailablePasskeysManually,
    openRemovePasskeysModal,
    removePasskey,
    updatePasskeys,
    getPasskeysData
};
